<!-- ============================================================================
  PlateX — Number Ones · The Register (approved design v23).
  Section order locked (Design Spec §1). Header/footer from the shared shell.
  Every registration links to its Plate Detail page (/plate/{REG}); view state
  + scroll are preserved for Back navigation via NumberOnesStateService.
============================================================================= -->

<!-- ============================ HERO ============================ -->
<section class="hero">
  <!-- Flat lead / figure / rest blocks so, on mobile, the Ferrari sits between
       the title and the intro (eyebrow → title → image → intro → actions →
       stats). Desktop keeps the approved two-column composition via grid areas. -->
  <div class="wrap hero-grid">
    <div class="hero-lead">
      <span class="eyebrow">The Collection · Blue-chip tier</span>
      <h1 class="hero-title">Number Ones.<br />The standing register.</h1>
    </div>
    <div class="hero-figure">
      <div class="visual"><img src="assets/pages/number-ones/hero_ferrari.jpg" alt="Ferrari wearing a black-and-silver Number One registration" /></div>
    </div>
    <div class="hero-rest">
      <p>A single digit paired with two letters, the rarest and most enduring format in the market. Fewer than a thousand exist. This is the complete register: the ones on the market, the ones held, and the ones that were never issued.</p>
      <div class="hero-cta">
        <a class="btn btn--green" (click)="jumpTo('registerZone', $event)" href="#registerZone">Enter the register</a>
        <a class="btn btn--ghost" (click)="jumpTo('specimen', $event)" href="#specimen">In focus this week</a>
      </div>
    </div>
  </div>
  <div class="wrap">
    <div class="hero-stats">
      <div class="s"><div class="n">958</div><div class="l">Two-letter · in existence · DVLA FOI</div></div>
      <div class="s"><div class="n">668</div><div class="l">Two-letter · documented &amp; located</div></div>
      <div class="s"><div class="n">{{ heroAvail }}</div><div class="l">Two-letter · on the market now</div></div>
      <div class="s"><div class="n">{{ registerHigh.value }}</div><div class="l">{{ registerHigh.label }}</div></div>
    </div>
  </div>
</section>

<!-- ============================ DEFINITION ============================ -->
<section class="section define">
  <div class="wrap">
    <div class="grid">
      <div>
        <span class="eyebrow" style="display:block;margin-bottom:16px">What a Number One is</span>
        <p class="stmt">The lowest number a set of initials can carry, apart from a handful of civic zeros.</p>
        <p>Issued once, decades ago, and never re-made: a Number One is a single <b>1</b> beside two letters. It reads as a name, a marque or a monogram, and because only one can ever exist for each pairing it behaves less like a plate and more like a title deed. Values have held through every market cycle since the 1950s.</p>
        <p>The register runs in two arrangements. Collectors read them differently, and the market prices them differently.</p>
      </div>
      <div class="arr-cards">
        <div class="arr-card">
          <a class="plate-link" [routerLink]="plateLink('1 NR')"><span class="plate-bs"><span class="ch">1 NR</span></span></a>
          <div class="t">Number first · “1 —”</div>
          <div class="d">The classic dateless form. Clean and symmetrical.</div>
        </div>
        <div class="arr-card">
          <a class="plate-link" [routerLink]="plateLink('JB 1')"><span class="plate-bs"><span class="ch">JB 1</span></span></a>
          <div class="t">Letters first · “— 1”</div>
          <div class="d">Reads as initials. JB 1 realised £608,600.</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============================ IN FOCUS (SPECIMEN) ============================ -->
<section class="section section--paper specimen jump-target" id="specimen">
  <div class="wrap">
    <div class="sec-head between">
      <div><span class="eyebrow">In Focus</span><h2 class="h-sec" style="margin-top:6px">One registration, examined.</h2></div>
      <a class="more" (click)="jumpTo('registerZone', $event)" href="#registerZone">The full register <span class="arw">→</span></a>
    </div>
    <div class="grid">
      <div class="stage">
        <a class="plate-link" [routerLink]="plateLink(focusReg.display)"><span class="plate-bs lg"><span class="ch">{{ focusReg.display }}</span></span></a>
      </div>
      <div>
        <span class="eyebrow">Dateless · Number first · Register ref. 1 NR</span>
        <h2>A dateless number one. Clean, symmetrical and endlessly transferable.</h2>
        <div class="spec-meta" *ngIf="focusLastSale">
          <div><div class="k">{{ focusLastSale.label }}</div><div class="v">{{ money(focusLastSale.price) }}</div></div>
        </div>
        <div class="compare">
          <div class="ch">
            <span>{{ focusReg.display }} · compared across verified sellers</span>
            <span class="n" *ngIf="focusSellers && focusSellers.length">{{ focusSellers.length }} seller{{ focusSellers.length === 1 ? '' : 's' }}</span>
            <span class="n" *ngIf="!focusSellers">—</span>
          </div>
          <div class="cr" *ngIf="!focusSellers"><span class="s">Checking verified sellers…</span><span class="rt"></span><span class="fi"></span><span class="pr">—</span></div>
          <div class="cr" *ngIf="focusSellers && focusSellers.length === 0"><span class="s">Not currently listed with verified sellers</span><span class="rt"></span><span class="fi"></span><span class="pr"></span></div>
          <div class="cr" *ngFor="let s of focusSellers" [class.best]="s.best">
            <span class="s">{{ s.name }} <span class="tag" *ngIf="s.best">Best price</span></span>
            <span class="rt" *ngIf="s.rating && s.rating !== '0'">★ {{ s.rating }}</span><span class="rt" *ngIf="!s.rating || s.rating === '0'"></span>
            <span class="fi" [class.na]="!s.finance">{{ s.finance ? 'Finance ✓' : '' }}</span>
            <span class="pr">{{ money(s.price) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============================ TIER I — THE PINNACLE ============================ -->
<section class="section tier-sec" id="tierPinnacle">
  <div class="wrap">
    <div class="tier-head">
      <span class="tier-ix">I</span>
      <div>
        <span class="eyebrow">The pinnacle · Single letters</span>
        <h2 class="h-sec">The single-letter ones.</h2>
        <p class="lead">Each letter exists as two parallel registrations, number-first and letters-first, shown here as equals. A couple of dozen exist in each arrangement; most are held for decades at a time.</p>
      </div>
    </div>
    <div class="letter-head">
      <span class="L">A to Z</span>
      <span class="c">both arrangements shown as equals · {{ pinnacleAvail === null ? '—' : pinnacleAvail }} available · {{ pinnacleExist === null ? '—' : pinnacleExist }} in existence</span>
    </div>
    <div class="pair-grid">
      <div class="pair-cell" *ngFor="let p of pinnaclePairs">
        <ng-container [ngTemplateOutlet]="cellTpl" [ngTemplateOutletContext]="{ $implicit: p.a }"></ng-container>
        <span class="pair-slash">/</span>
        <ng-container [ngTemplateOutlet]="cellTpl" [ngTemplateOutletContext]="{ $implicit: p.b }"></ng-container>
      </div>
    </div>
  </div>
</section>

<!-- ============================ TIER II — THE REGISTER ============================ -->
<div id="registerZone" class="jump-target">
  <div class="reg-heading">
    <div class="wrap">
      <!-- Tier II carries the same Roman-numeral marker as I and III so the
           three tiers read as one sequence (I Pinnacle, II Register, III Field). -->
      <div class="tier-head">
        <span class="tier-ix">II</span>
        <div>
          <span class="eyebrow">The register · Two letters</span>
          <h2 class="h-sec">Every two-letter Number One.</h2>
          <p class="lead">The complete, finite set of 676 pairings in each arrangement. This is the heart of the collection, and the only tier a matrix can hold.</p>
        </div>
      </div>
    </div>
  </div>

  <div class="toolbar jump-target" id="register">
    <div class="wrap">
      <span class="tb-lbl">View</span>
      <div class="tb-group">
        <button [class.active]="view === 'catalogue'" (click)="setView('catalogue')">Catalogue</button>
        <button [class.active]="view === 'matrix'" (click)="setView('matrix')">Register matrix</button>
      </div>
      <span class="tb-lbl">Arrangement</span>
      <div class="tb-group">
        <button [class.active]="arr === 1" (click)="setArr(1)">1 —</button>
        <button [class.active]="arr === 2" (click)="setArr(2)">— 1</button>
      </div>
      <span class="tb-lbl">Show</span>
      <div class="tb-group">
        <button [class.active]="avail === 'all'" (click)="setAvail('all')">All</button>
        <button [class.active]="avail === 'available'" (click)="setAvail('available')">For sale</button>
        <button [class.active]="avail === 'held'" (click)="setAvail('held')">Held</button>
      </div>
      <!-- held = evidence-backed existing/held; never-issued still awaits a
           per-pairing reference (neutral placeholder, never fabricated) -->
      <span class="tb-count"><b>{{ availCount === null ? '—' : availCount }}</b> on the market · <b>{{ heldCount === null ? '—' : heldCount }}</b> held · <b>—</b> never issued</span>
    </div>
  </div>

  <div class="azbar" id="regAz" [class.hidden]="view !== 'catalogue'">
    <div class="wrap">
      <!-- one first letter at a time: picking a letter swaps the catalogue
           below rather than scrolling through all 676 pairings -->
      <span class="azlinks" role="tablist" aria-label="First letter">
        <button *ngFor="let L of letters" type="button" role="tab"
                [class.on]="L === catLetter" [attr.aria-selected]="L === catLetter"
                [disabled]="!catCounts[L]"
                [attr.title]="catCounts[L] ? L + ': ' + catCounts[L] + ' shown' : L + ': none'"
                (click)="setCatLetter(L)">{{ L }}</button>
      </span>
      <span class="legend">
        <span><i class="lg-av"></i>On the market</span>
        <span><i class="lg-hd"></i>Held</span>
        <span><i class="lg-gh"></i>Never issued</span>
      </span>
    </div>
  </div>

  <section class="register">
    <div class="wrap">
      <div [class.hidden]="view !== 'catalogue'">
        <ng-container *ngIf="catGroup as g" [ngTemplateOutlet]="groupTpl" [ngTemplateOutletContext]="{ $implicit: g }"></ng-container>
        <p class="cat-empty" *ngIf="!catGroup && !loading">Nothing in the register matches this filter.</p>
        <div class="cat-steps" *ngIf="catGroup">
          <a class="cat-step" *ngIf="catStep(-1) as p" href="#regAz" (click)="setCatLetter(p, $event)"><span class="arw">←</span> {{ p }}</a>
          <span class="cat-mid">Register · letter {{ catLetter }}</span>
          <a class="cat-step nx" *ngIf="catStep(1) as n" href="#regAz" (click)="setCatLetter(n, $event)">{{ n }} <span class="arw">→</span></a>
        </div>
      </div>

      <div [class.hidden]="view !== 'matrix'">
        <div class="matrix-wrap">
          <div class="matrix">
            <div class="corner"></div>
            <div class="hd" *ngFor="let L of letters">{{ L }}</div>
            <ng-container *ngFor="let row of matrixRows">
              <div class="rw">{{ row.letter }}</div>
              <ng-container *ngFor="let x of row.cells">
                <div *ngIf="x && x.state === 'available'" class="mcell available clickable" (click)="openPlate(x.combo)">{{ x.combo }}<span class="tip">{{ x.combo }} · {{ x.price === undefined ? 'POA' : 'from ' + money(x.price) }}</span></div>
                <div *ngIf="x && (x.state === 'held' || x.state === 'existing')" class="mcell held clickable" (click)="openPlate(x.combo)">{{ x.combo }}<span class="tip">{{ x.combo }} · {{ x.state === 'existing' ? 'existing' : 'held' }}</span></div>
                <div *ngIf="!x || x.state === 'ghost' || x.state === 'no-information' || x.state === 'lost'" class="mcell ghost"></div>
              </ng-container>
            </ng-container>
          </div>
        </div>
        <p class="matrix-note">Rows are the first letter, columns the second. Hover any entry to enlarge it. Empty squares were never issued. The gaps are part of the register. Showing the <b>{{ arrToken }}</b> arrangement.</p>
      </div>
    </div>
  </section>
</div>

<!-- ============================ TIER III — THE WIDER FIELD ============================ -->
<section class="section tier-sec" id="tierField">
  <div class="wrap">
    <div class="tier-head">
      <span class="tier-ix">III</span>
      <div>
        <span class="eyebrow">The wider field · Three letters</span>
        <h2 class="h-sec">Explore three-letter Number Ones.</h2>
        <p class="lead">Beyond the register lies the wider world of personal initials, thousands of three-letter ones carrying names, monograms and marques. Too many to browse; made to be searched.</p>
      </div>
    </div>
    <div class="field-stats">
      <div class="fstat"><div class="fk">The universe</div><div class="fv">17,576</div><div class="fc">theoretical combinations, in each arrangement</div></div>
      <div class="fstat"><div class="fk">Issued</div><div class="fv"><span>{{ threeIssued }}</span></div><div class="fc">actually assigned over the decades</div></div>
      <div class="fstat"><div class="fk">Available now</div><div class="fv"><span class="livedot" *ngIf="threeAvail"></span><span>{{ threeAvail === null ? '—' : threeAvail }}</span></div><div class="fc">on the market today, read live from the register</div></div>
    </div>
    <form class="field-search-row" (submit)="$event.preventDefault(); runFieldSearch(fieldTerm)">
      <div class="field-search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
        <input #fieldInputEl type="text" maxlength="5" [value]="fieldTerm" (input)="fieldInput($any($event.target).value); fieldInputEl.value = fieldTerm" placeholder="Search three-letter registrations, your initials or a name…" />
      </div>
      <button class="btn btn--green" type="submit">Search the field</button>
    </form>

    <!-- inline Number Ones results (letters-only wildcard search over the
         live three-letter inventory; deduped; both arrangements) -->
    <div class="f3-results" *ngIf="threeResults !== null">
      <p class="field-note" *ngIf="threeResults.length"><b>{{ threeResults.length }}</b> three-letter Number One{{ threeResults.length === 1 ? '' : 's' }} on the market matching <b>{{ fieldTerm }}</b>:</p>
      <p class="field-note" *ngIf="!threeResults.length">No three-letter Number Ones currently on the market match <b>{{ fieldTerm }}</b>.</p>
      <div class="f3-row" *ngFor="let r of threeResults">
        <a class="plate-link" [routerLink]="plateLink(r.combo)"><span class="plate-bs f3-plate"><span class="ch">{{ r.combo }}</span></span></a>
        <span class="f3-acts">
          <pla-inline-favourite [plate]="r.reg" [showText]="false"></pla-inline-favourite>
          <pla-inline-plate-viewer-link [plate]="r.reg" [iconOnly]="true"></pla-inline-plate-viewer-link>
        </span>
        <span class="f3-price"><em>FROM</em> {{ money(r.price) }}</span>
        <a class="f3-view" [routerLink]="plateLink(r.combo)">View</a>
      </div>
    </div>

    <p class="field-foot">The universe is fixed (26³ per arrangement). Issued and available are read live from the register. The figures shown here are modelled placeholders, not verified counts.</p>
  </div>
</section>

<!-- ============================ THE PLATEX REVIEW ============================ -->
<section class="section review-intro" id="reviewIntro">
  <div class="wrap">
    <span class="ri-kicker">The PlateX Review</span>
    <div class="ri-rule"></div>
    <p class="ri-stand">Three readings of a single registration: its history, its market, and the research behind what it is worth.</p>
  </div>
</section>

<!-- ============================ CHAPTER I — HISTORY / PROVENANCE ============================ -->
<section class="section prov-band" id="provSec">
  <div class="wrap">
    <div class="grid">
      <div class="frame">
        <img src="assets/pages/number-ones/oa1_archival_bw.jpg" alt="Archival photograph of OA 1, an early Liverpool registration" />
      </div>
      <div>
        <span class="eyebrow"><span class="chap-n">Chapter I</span>History · Provenance</span>
        <h2>Every number one carries a history worth keeping.</h2>
        <p>The earliest of these registrations were issued more than a century ago and have passed through generations of ownership. Part of the register's value is the record itself: where a plate has been, and what it has worn.</p>
        <p class="cap">Archival · OA 1, an early Liverpool registration. Caption to be finalised from the lot's provenance.</p>
      </div>
    </div>
  </div>
</section>

<!-- ============================ CHAPTER II — MARKET / VERIFIED SALES ============================ -->
<section class="section section--paper" id="salesSec">
  <div class="wrap">
    <div class="sec-head between">
      <div><span class="eyebrow"><span class="chap-n">Chapter II</span>Market · Verified sales</span><h2 class="h-sec" style="margin-top:6px">Verified Number One sales</h2></div>
      <a class="more">Full sales archive <span class="arw">→</span></a>
    </div>
    <div class="vsales">
      <div class="vrow" *ngIf="salesRows === null"><span class="plate-bs empty sm"></span><span class="src">Loading verified sales…</span><span class="vf"></span><span class="pr">—</span></div>
      <div class="vrow" *ngIf="salesRows && salesRows.length === 0"><span class="plate-bs empty sm"></span><span class="src">Verified sales are temporarily unavailable.</span><span class="vf"></span><span class="pr"></span></div>
      <div class="vrow" *ngFor="let s of salesRows">
        <a class="plate-link" [routerLink]="plateLink(s.combo)"><span class="plate-bs sm"><span class="ch">{{ s.combo }}</span></span></a>
        <span class="src">{{ s.source }}</span>
        <span class="vf">Verified</span>
        <span class="pr">{{ money(s.price) }}</span>
      </div>
    </div>
  </div>
</section>

<!-- ============================ CHAPTER III — RESEARCH ============================ -->
<section class="section iv-sec" id="ivSec">
  <div class="wrap">
    <div class="iv-head">
      <span class="eyebrow"><span class="chap-n">Chapter III</span>Research · Your initial &amp; value</span>
      <p class="stmt">How valuable is your initial?</p>
      <p class="lead">Every Number One begins with a letter. Some are everywhere; some are rare. But does a common initial command a higher price, or is value written somewhere else? <b>Are the most common first initials also the most valuable?</b></p>
    </div>

    <div class="iv-find">
      <label for="ivInput">Find your initial</label>
      <input id="ivInput" maxlength="1" placeholder="A-Z" autocomplete="off" spellcheck="false" [value]="ivInputValue" (input)="ivInput($any($event.target).value)" />
      <div class="iv-az">
        <button *ngFor="let l of letters" [class.on]="l === ivSel" (click)="ivSelect(l)">{{ l }}</button>
      </div>
    </div>

    <div class="iv-main">
      <div class="iv-plotwrap">
        <svg class="iv-chart" [attr.viewBox]="'0 0 ' + ivW + ' ' + ivH" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Scatter plot: first-initial popularity against plate value, one mark per letter A to Z. ONS births data and PlateX verified sales.">
          <ng-container *ngFor="let g of ivGrid">
            <line [attr.x1]="gx(g)" [attr.x2]="gx(g)" [attr.y1]="ivPlot.t" [attr.y2]="ivPlot.b" class="iv-grid" />
            <line [attr.x1]="ivPlot.l" [attr.x2]="ivPlot.r" [attr.y1]="gy(g)" [attr.y2]="gy(g)" class="iv-grid" />
          </ng-container>
          <line [attr.x1]="ivPlot.l" [attr.x2]="ivPlot.r" [attr.y1]="ivPlot.b" [attr.y2]="ivPlot.b" class="iv-axis" />
          <line [attr.x1]="ivPlot.l" [attr.x2]="ivPlot.l" [attr.y1]="ivPlot.t" [attr.y2]="ivPlot.b" class="iv-axis" />
          <line [attr.x1]="ivTrend.x1" [attr.y1]="ivTrend.y1" [attr.x2]="ivTrend.x2" [attr.y2]="ivTrend.y2" class="iv-trend" />
          <text [attr.x]="ivPlot.r" [attr.y]="ivPlot.b - 14" class="iv-trendlbl">computed trend · r = {{ ivTrendR }}</text>
          <text [attr.x]="ivPlot.l + (ivPlot.r - ivPlot.l) / 2" [attr.y]="ivH - 14" class="iv-title">HOW COMMON THE FIRST INITIAL IS →</text>
          <text [attr.x]="17" [attr.y]="ivYTitleY" class="iv-title" [attr.transform]="'rotate(-90 17 ' + ivYTitleY + ')'">PLATE VALUE →</text>
          <g *ngFor="let m of ivMarks" class="iv-mark" [class.sparse]="m.sparse" [class.sel]="m.letter === ivSel" tabindex="0" role="button" [attr.aria-label]="m.letter" (click)="ivSelect(m.letter)" (keydown.enter)="ivSelect(m.letter)" (keydown.space)="$event.preventDefault(); ivSelect(m.letter)">
            <circle [attr.cx]="m.x" [attr.cy]="m.y" [attr.r]="m.letter === ivSel ? 17 : 12" class="iv-dot" />
            <text [attr.x]="m.x" [attr.y]="m.y" class="iv-lt">{{ m.letter }}</text>
            <title>{{ m.title }}</title>
          </g>
        </svg>
      </div>
      <aside class="iv-readout">
        <div class="ro-letter">{{ ivSel }}</div>
        <div class="ro-row"><span class="ro-k">Popularity</span><span class="ro-bar"><i [style.width.%]="ivSelData.pop"></i></span><span class="ro-v">#{{ rankPop(ivSel) }} of 26</span></div>
        <div class="ro-row"><span class="ro-k">Plate value</span><span class="ro-bar val"><i [style.width.%]="ivSelData.val"></i></span><span class="ro-v">#{{ rankVal(ivSel) }} of 26{{ ivSelData.ev === 'sparse' ? ' · sparse evidence' : '' }}</span></div>
        <p class="ro-note">{{ ivSelData.note }}</p>
        <p class="ro-illus">Popularity ← ONS first-name initial frequency (2016 to 2025). Value ← PlateX verified auction sales, weighted median per initial.</p>
      </aside>
    </div>

    <div class="iv-findings">
      <div class="find-card" *ngFor="let f of ivFindings; let i = index"><span class="fnum">0{{ i + 1 }}</span><p>{{ f }}</p></div>
    </div>

    <p class="iv-source">Source: ONS Baby names in England and Wales, 2016 to 2025; PlateX verified sales. Popularity reflects first-name initials among births in England and Wales, not two-letter personal initials or the UK adult population.</p>
  </div>
</section>

<!-- ============================ REGISTER INTEREST (CLOSE) ============================ -->
<section class="close-sec" id="closeSec">
  <div class="wrap">
    <div class="grid">
      <div>
        <span class="eyebrow" style="display:block;margin-bottom:16px">Register your interest</span>
        <p class="stmt">Watch a pairing. We'll tell you the moment it moves.</p>
        <p>Most Number Ones change hands quietly, before they're ever listed. Register the initials you're looking for and we'll alert you across every verified seller, and when one comes out of a private collection.</p>
      </div>
      <form class="reg-form" (submit)="$event.preventDefault(); submitWatch(pairInput.value, emailInput.value)">
        <div class="fld"><label>The pairing you want</label><input #pairInput type="text" placeholder="e.g. 1 HS  ·  or  HS 1" autocomplete="off" /></div>
        <div class="fld"><label>Email</label><input #emailInput type="email" placeholder="you@example.com" autocomplete="off" /></div>
        <button class="btn btn--green" style="width:100%;justify-content:center" type="submit" [disabled]="watchStatus === 'saving' || watchStatus === 'added'">
          {{ watchStatus === 'saving' ? 'Adding…' : watchStatus === 'added' ? 'Added to your register watch ✓' : 'Add to my register watch' }}
        </button>
        <p class="watch-note" *ngIf="watchStatus === 'invalid'">Enter the letters of the pairing you want, one to three letters (e.g. HS).</p>
        <p class="watch-note" *ngIf="watchStatus === 'added'">Watching {{ '1 ' + watchPair }} and {{ watchPair + ' 1' }}. We'll alert you when either moves.</p>
      </form>
    </div>
  </div>
</section>

<!-- ============ shared cell template (catalogue + pinnacle pairs) ============
     Every slot communicates something and every registration is a link. ==== -->
<ng-template #cellTpl let-x>
  <a *ngIf="x.state === 'available'" class="cell available" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs"><span class="ch">{{ x.combo }}</span></span>
    <span class="st">{{ money(x.price) }}</span>
  </a>
  <a *ngIf="x.state === 'held'" class="cell held" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs"><span class="ch">{{ x.combo }}</span></span>
    <span class="st">Held</span>
  </a>
  <a *ngIf="x.state === 'existing'" class="cell held existing" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs"><span class="ch">{{ x.combo }}</span></span>
    <span class="st">Existing</span>
  </a>
  <a *ngIf="x.state === 'lost'" class="cell ghost lost" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs empty"><span class="ch gh">{{ x.combo }}</span></span>
    <span class="st">Lost</span>
  </a>
  <a *ngIf="x.state === 'ghost'" class="cell ghost" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs empty"><span class="ch gh">{{ x.combo }}</span></span>
    <span class="st">Never issued</span>
  </a>
  <a *ngIf="x.state === 'no-information'" class="cell ghost noinfo" [routerLink]="plateLink(x.combo)">
    <span class="plate-bs empty"><span class="ch gh">{{ x.combo }}</span></span>
    <span class="st">No information</span>
  </a>
</ng-template>

<ng-template #groupTpl let-g>
  <div class="letter-group" [id]="'grp-' + g.letter">
    <div class="letter-head">
      <span class="L">{{ g.letter }}</span>
      <span class="c">{{ g.avail }} on the market · {{ g.inRegister }} in the register</span>
    </div>
    <div class="cat-grid">
      <ng-container *ngFor="let x of g.cells" [ngTemplateOutlet]="cellTpl" [ngTemplateOutletContext]="{ $implicit: x }"></ng-container>
    </div>
  </div>
</ng-template>
